import { useCallback, useRef, useState } from "react";

// Intervalle de vérification du silence (en ms)
const SILENCE_CHECK_INTERVAL_MS = 100;

interface UseAudioRecorderOptions {
  onTranscript: (blob: Blob, mimeType: string) => Promise<void>;
  onError?: (error: string) => void;
  onMaxDurationReached?: () => void;
  onSilenceDetected?: () => void;
  onVolumeChange?: (volume: number) => void;
  silenceTimeoutSeconds?: number;
  silenceThreshold?: number;
}

interface UseAudioRecorderReturn {
  isRecording: boolean;
  isSupported: boolean;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
}

function getSupportedMimeType(): string {
  const types = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/ogg;codecs=opus",
  ];
  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) return type;
  }
  return "";
}

export function useAudioRecorder({
  onTranscript,
  onError,
  onMaxDurationReached,
  onSilenceDetected,
  onVolumeChange,
  silenceTimeoutSeconds = Number(
    process.env.BUN_PUBLIC_SILENCE_TIMEOUT_SECONDS || 10,
  ),
  silenceThreshold = Number(process.env.BUN_PUBLIC_SILENCE_THRESHOLD || 0.05),
}: UseAudioRecorderOptions): UseAudioRecorderReturn {
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const maxDurationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  // refs pour la détection de silence
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const silenceCheckIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const silenceStartRef = useRef<number | null>(null);
  const silenceThresholdRef = useRef<number>(silenceThreshold);
  const stoppedBySilenceRef = useRef<boolean>(false);
  const hasSpeechDetectedRef = useRef<boolean>(false);

  const isSupported =
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== "undefined";

  const cleanupAudioAnalysis = useCallback(() => {
    if (silenceCheckIntervalRef.current) {
      clearInterval(silenceCheckIntervalRef.current);
      silenceCheckIntervalRef.current = null;
    }
    if (audioContextRef.current) {
      void audioContextRef.current.close();
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    silenceStartRef.current = null;
  }, []);

  const stopRecording = useCallback(() => {
    // Annule le minuteur de durée maximale
    if (maxDurationTimerRef.current) {
      clearTimeout(maxDurationTimerRef.current);
      maxDurationTimerRef.current = null;
    }

    // Libère l'analyse audio
    cleanupAudioAnalysis();

    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }
    setIsRecording(false);
  }, [cleanupAudioAnalysis]);

  const checkSilence = useCallback(() => {
    if (!analyserRef.current) return;

    const analyser = analyserRef.current;
    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteTimeDomainData(dataArray);

    // Calcul RMS (Root Mean Square) du volume
    let sum = 0;
    for (let i = 0; i < dataArray.length; i++) {
      const normalized = ((dataArray[i] as number) - 128) / 128;
      sum += normalized * normalized;
    }
    const rms = Math.sqrt(sum / dataArray.length);

    // Exposer le volume normalisé (0.0 à 1.0) via callback
    // On mappe rms jusqu'à ~0.3 pour le max perceptible
    const normalizedVolume = Math.min(rms / 0.3, 1);
    onVolumeChange?.(normalizedVolume);

    const now = Date.now();
    const silenceTimeoutMs = silenceTimeoutSeconds * 1000;

    if (rms < silenceThresholdRef.current) {
      // Silence détecté
      if (silenceStartRef.current === null) {
        silenceStartRef.current = now;
      } else if (now - silenceStartRef.current >= silenceTimeoutMs) {
        // Si aucune parole n'a jamais été détectée, ne pas transcrire
        if (!hasSpeechDetectedRef.current) {
          stoppedBySilenceRef.current = true;
        }
        onSilenceDetected?.();
        stopRecording();
      }
    } else {
      // Parole détectée -> reset du timer de silence et marquer que l'utilisateur a parlé
      silenceStartRef.current = null;
      hasSpeechDetectedRef.current = true;
    }
  }, [silenceTimeoutSeconds, onSilenceDetected, onVolumeChange, stopRecording]);

  const startRecording = useCallback(async () => {
    if (!isSupported) {
      onError?.("L'enregistrement audio n'est pas supporté par ce navigateur.");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = getSupportedMimeType();
      const mediaRecorder = new MediaRecorder(
        stream,
        mimeType ? { mimeType } : undefined,
      );

      chunksRef.current = [];
      stoppedBySilenceRef.current = false;
      hasSpeechDetectedRef.current = false;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        // Annule le minuteur de durée maximale s'il tourne encore
        if (maxDurationTimerRef.current) {
          clearTimeout(maxDurationTimerRef.current);
          maxDurationTimerRef.current = null;
        }

        // Libère l'analyse audio
        cleanupAudioAnalysis();

        // Si l'enregistrement a été arrêté par silence sans parole, ne pas transcrire
        if (stoppedBySilenceRef.current) {
          stoppedBySilenceRef.current = false;
          // Relâche le micro
          streamRef.current?.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
          chunksRef.current = [];
          return;
        }

        const actualMimeType =
          mediaRecorder.mimeType || mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type: actualMimeType });
        chunksRef.current = [];

        // Relâche le micro
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;

        await onTranscript(blob, actualMimeType);
      };

      mediaRecorder.start();
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);

      // Met en place l'analyse audio nécessaire à la détection de silence
      try {
        const audioContext = new AudioContext();
        const source = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 2048;
        source.connect(analyser);

        audioContextRef.current = audioContext;
        analyserRef.current = analyser;
        silenceStartRef.current = null;

        // Démarre la vérification périodique du silence
        silenceCheckIntervalRef.current = setInterval(
          checkSilence,
          SILENCE_CHECK_INTERVAL_MS,
        );
      } catch {
        console.warn("Impossible d'initialiser la détection de silence");
      }

      // Minuteur d'arrêt automatique au bout de la durée maximale
      const maxRecordingDurationMs =
        Number(process.env.BUN_PUBLIC_MAX_RECORDING_DURATION_MINUTES || 5) *
        60 *
        1000;
      maxDurationTimerRef.current = setTimeout(() => {
        if (
          mediaRecorderRef.current &&
          mediaRecorderRef.current.state === "recording"
        ) {
          onMaxDurationReached?.();
          stopRecording();
        }
      }, maxRecordingDurationMs);
    } catch (err) {
      if (err instanceof DOMException && err.name === "NotAllowedError") {
        onError?.(
          "Accès au microphone refusé. Veuillez autoriser l'accès dans les paramètres du navigateur.",
        );
      } else {
        onError?.("Impossible de démarrer l'enregistrement audio.");
      }
    }
  }, [
    isSupported,
    onTranscript,
    onError,
    onMaxDurationReached,
    cleanupAudioAnalysis,
    checkSilence,
    stopRecording,
  ]);

  return { isRecording, isSupported, startRecording, stopRecording };
}
